import { CSSProperties } from 'react';

// Configuration interface for full customization
interface CasinoLandingCardConfig {
  heading: string;
  subheading: string;
  checklistItems: string[];
  ctaText: string;
  onCtaClick?: () => void;
  colors?: {
    cardBackgroundGradientStart?: string;
    cardBackgroundGradientEnd?: string;
    cardGlowColor?: string;
    borderColor?: string;
    headingColor?: string;
    subheadingColor?: string;
    checklistTextColor?: string;
    checkmarkGradientStart?: string;
    checkmarkGradientEnd?: string;
    checkmarkIconColor?: string;
    buttonGradientStart?: string;
    buttonGradientEnd?: string;
    buttonTextColor?: string;
    buttonGlowColor?: string;
  };
  sizing?: {
    headingFontSize?: string;
    subheadingFontSize?: string;
    checklistFontSize?: string;
    buttonFontSize?: string;
    checkmarkSize?: string;
    cardPadding?: string;
    borderRadius?: string;
    borderWidth?: string;
    maxWidth?: string;
  };
}

// Default configuration matching the design
const defaultConfig: Required<Omit<CasinoLandingCardConfig, 'onCtaClick'>> & { onCtaClick?: () => void } = {
  heading: 'For Casino Operators',
  subheading: 'Scale into New Markets—instantly',
  checklistItems: [
    'Expand into new GEOs 1000x faster—no dev team needed',
    'Change design post-launch with zero downtime',
    'Switch from outdated providers and go crypto-friendly',
    'Full-stack solution: payments, KYC/AML, risk, CRM, affiliates',
    'All day-to-day operations handled for you'
  ],
  ctaText: 'SCALE FASTER WITH MAINCARD',
  onCtaClick: undefined,
  colors: {
    cardBackgroundGradientStart: 'rgba(0, 0, 0, 0.3)',
    cardBackgroundGradientEnd: 'rgba(0, 0, 0, 0.2)',
    cardGlowColor: 'rgba(var(--color1), 0.25)',
    borderColor: 'rgba(var(--color1), 0.3)',
    headingColor: '#FFFFFF',
    subheadingColor: 'rgba(255, 255, 255, 0.8)',
    checklistTextColor: 'rgba(255, 255, 255, 0.8)',
    checkmarkGradientStart: 'rgb(var(--color1))',
    checkmarkGradientEnd: 'rgb(var(--color2))',
    checkmarkIconColor: '#ffffff',
    buttonGradientStart: 'rgb(var(--color1))',
    buttonGradientEnd: 'rgb(var(--color2))',
    buttonTextColor: '#ffffff',
    buttonGlowColor: 'rgba(var(--color1), 0.6)'
  },
  sizing: {
    headingFontSize: '32px',
    subheadingFontSize: '16px',
    checklistFontSize: '14px',
    buttonFontSize: '14px',
    checkmarkSize: '20px',
    cardPadding: '2.5rem 3.5rem',
    borderRadius: '24px',
    borderWidth: '1px',
    maxWidth: '920px'
  }
};

export default function CasinoLandingCard(userConfig?: CasinoLandingCardConfig) {
  // Merge user config with defaults
  const config = {
    ...defaultConfig,
    ...userConfig,
    colors: {
      ...defaultConfig.colors,
      ...userConfig?.colors
    },
    sizing: {
      ...defaultConfig.sizing,
      ...userConfig?.sizing
    }
  };

  const handleCtaClick = () => {
    if (config.onCtaClick) {
      config.onCtaClick();
    } else {
      console.log('CTA button clicked');
    }
  };

  // Inline styles using configuration
  const cardStyle: CSSProperties = {
    position: 'relative',
    background: `linear-gradient(135deg, ${config.colors.cardBackgroundGradientStart} 0%, ${config.colors.cardBackgroundGradientEnd} 100%)`,
    border: `${config.sizing.borderWidth} solid ${config.colors.borderColor}`,
    borderRadius: config.sizing.borderRadius,
    padding: config.sizing.cardPadding,
    maxWidth: config.sizing.maxWidth,
    width: '100%',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    boxShadow: `0 0 40px ${config.colors.cardGlowColor}`,
    overflow: 'hidden'
  };

  const innerGlowStyle: CSSProperties = {
    position: 'absolute',
    width: '500px',
    height: '500px',
    background: `radial-gradient(circle, rgba(var(--color1), 0.3) 0%, rgba(var(--color2), 0.2) 30%, transparent 70%)`,
    borderRadius: '50%',
    pointerEvents: 'none',
    zIndex: 0,
    animation: 'floatingGlow 15s ease-in-out infinite'
  };

  const contentWrapperStyle: CSSProperties = {
    position: 'relative',
    zIndex: 1
  };

  const contentGridStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    alignItems: 'center'
  };

  const headingStyle: CSSProperties = {
    fontSize: config.sizing.headingFontSize,
    fontWeight: 700,
    lineHeight: 1.2,
    color: config.colors.headingColor,
    margin: 0,
    marginBottom: '12px',
    textShadow: '0 0 10px rgba(255, 255, 255, 0.15)'
  };

  const subheadingStyle: CSSProperties = {
    fontSize: config.sizing.subheadingFontSize,
    fontWeight: 400,
    lineHeight: 1.6,
    color: config.colors.subheadingColor,
    margin: 0
  };

  const checklistStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const checklistItemStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    fontSize: config.sizing.checklistFontSize,
    fontWeight: 400,
    lineHeight: 1.6,
    color: config.colors.checklistTextColor
  };

  const checkmarkStyle: CSSProperties = {
    width: config.sizing.checkmarkSize,
    height: config.sizing.checkmarkSize,
    minWidth: config.sizing.checkmarkSize,
    background: `linear-gradient(135deg, ${config.colors.checkmarkGradientStart} 0%, ${config.colors.checkmarkGradientEnd} 100%)`,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '2px',
    boxShadow: '0 0 8px rgba(255, 0, 200, 0.3)'
  };

  const buttonWrapperStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '28px'
  };

  const buttonStyle: CSSProperties = {
    background: `linear-gradient(90deg, ${config.colors.buttonGradientStart} 0%, ${config.colors.buttonGradientEnd} 100%)`,
    color: config.colors.buttonTextColor,
    fontSize: config.sizing.buttonFontSize,
    fontWeight: 700,
    letterSpacing: '0.5px',
    padding: '14px 32px',
    borderRadius: '9999px',
    border: 'none',
    cursor: 'pointer',
    boxShadow: `0 0 35px ${config.colors.buttonGlowColor}, 0 0 60px ${config.colors.buttonGlowColor}`,
    transition: 'all 0.3s ease',
    textTransform: 'none' as const
  };

  return (
    <div style={cardStyle} data-testid="card-casino-landing">
      {/* Inner gradient glow effect */}
      <div style={innerGlowStyle}></div>
      
      {/* Content wrapper */}
      <div style={contentWrapperStyle}>
        <div style={contentGridStyle}>
          {/* Left Column - Heading Section */}
          <div>
            <h2 style={headingStyle} data-testid="text-heading">
              {config.heading}
            </h2>
            <p style={subheadingStyle} data-testid="text-subheading">
              {config.subheading}
            </p>
          </div>

          {/* Right Column - Checklist */}
          <ul style={checklistStyle} data-testid="list-benefits">
            {config.checklistItems.map((item, index) => (
              <li key={index} style={checklistItemStyle} data-testid={`item-benefit-${index}`}>
                <div style={checkmarkStyle}>
                  <svg
                    width="12"
                    height="10"
                    viewBox="0 0 14 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    data-testid={`icon-checkmark-${index}`}
                  >
                    <path
                      d="M1 5.5L5 9.5L13 1.5"
                      stroke={config.colors.checkmarkIconColor}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA Button */}
        <div style={buttonWrapperStyle}>
          <button
            style={buttonStyle}
            onClick={handleCtaClick}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = `0 0 50px ${config.colors.buttonGlowColor}, 0 0 80px ${config.colors.buttonGlowColor}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = `0 0 35px ${config.colors.buttonGlowColor}, 0 0 60px ${config.colors.buttonGlowColor}`;
            }}
            data-testid="button-cta"
          >
            {config.ctaText}
          </button>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes floatingGlow {
          0% {
            transform: translate(-20%, 100%);
          }
          25% {
            transform: translate(60%, -30%);
          }
          50% {
            transform: translate(80%, 80%);
          }
          75% {
            transform: translate(10%, -20%);
          }
          100% {
            transform: translate(-20%, 100%);
          }
        }

        @media (max-width: 767px) {
          [data-testid="card-casino-landing"] {
            padding: 2rem !important;
          }
          
          [data-testid="card-casino-landing"] > div:nth-child(2) > div:first-child {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }

          [data-testid="text-heading"] {
            font-size: 24px !important;
          }

          [data-testid="text-subheading"] {
            font-size: 14px !important;
          }

          [data-testid="button-cta"] {
            width: 100%;
            font-size: 13px !important;
            padding: 12px 24px !important;
          }
        }
      `}</style>
    </div>
  );
}
